import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import style from './amazon.module.scss'
import useSession from '@aytm/app/swidget/base/session/useSession.ts'
import { Dialog } from 'primereact/dialog'
import Item from '@aytm/app/swidget/base/questions/Ecommerce/Amazon/Item.tsx'
import Header from '@aytm/app/swidget/base/questions/Ecommerce/Amazon/Header.tsx'
import Footer from '@aytm/app/swidget/base/questions/Ecommerce/Amazon/Footer.tsx'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import Cart from '@aytm/app/swidget/base/questions/Ecommerce/Amazon/Cart.tsx'
import Main from './ViewComponents/Main.tsx'
import { IEcommerceQuestionViewProps } from '../types.ts'
import useTimeline from '../../../modules/timeline/useTimeline.ts'
import Aside from './ViewComponents/Aside.tsx'
import AppMobileHeader from './appMobile/AppMobileHeader.tsx'
import AppMobileFooter from './appMobile/AppMobileFooter.tsx'
import useTheme from '../../../modules/theme/useTheme.ts'
import ExitSimulation from '../../_common/ExitSimulation.tsx'
import useSurveyLocale from '../../../modules/locale/useSurveyLocale.ts'
import RightColumn from './RIghtColumn.tsx'
import useLoadScriptInFrame from '@aytm/app/swidget/base/questions/Ecommerce/hooks/useLoadScriptInFrame.ts'
import { countDrawnCards } from '@aytm/app/swidget/base/questions/Ecommerce/utils/hiddenClass.ts'

const AmazonView = ({
  schema,
  response,
  // _responseValidation,
  // _timeline,
  // _showValidation,
  checkout,
  setClosed,
  onUpdateResponse
}: IEcommerceQuestionViewProps) => {
  const session = useSession()
  const na = useMemo(() => schema.answers.find(answer => answer.na), [schema])
  // the header counts the cards the grid renders: children sit inside their parent's card, banner and
  // featured products have rows of their own, none-of-the-above is not a product, and a preview-hidden card is
  // not drawn
  const resultsCount = useMemo(
    () =>
      countDrawnCards(
        schema.answers.filter(
          answer => !answer.featured && !answer.banner && !answer.na && null === answer.parent_product
        )
      ),
    [schema]
  )
  const totals = [
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v), 0),
    response.answers.reduce((prev, cur) => prev + cur.v * cur.price, 0) || 0
  ]
  const viewport = useViewport()
  const findability = 'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)
  const showRightColumn = !viewport.mobile && totals[0] > 0 && !findability
  const productsPerRow =
    schema.products_per_row || Math.ceil(((viewport.width || 2200) - (showRightColumn ? 380 : 250)) / 270)
  // const selectedIndex = response.answers.findIndex(r => r.selected)
  const [showCart, setShowCart] = useState(false)
  const lightboxRef = useRef<Dialog>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollPoint = useRef<number>(null)
  const timeline = useTimeline()
  const mainWidth = (viewport.width || 2200) - (viewport.mobile ? 0 : showRightColumn ? 392 : 270)
  const ceilWidth = mainWidth / productsPerRow - 10 - 14 / productsPerRow
  const theme = useTheme()
  const { t } = useSurveyLocale()
  const [needHint, setNeedHint] = useState(true)
  const [rightColumnProductIndex, setRightColumnProductIndex] = useState(-1)
  const offsetCorrection = viewport.mobile ? 200 : 360
  useLoadScriptInFrame('https://unpkg.com/@google/model-viewer', containerRef, viewport.mobile && viewport.emulated)

  const saveScrollPoint = (event: React.MouseEvent) => {
    if (!containerRef.current) return
    const targetElement = event.currentTarget
    const elementRect = targetElement.getBoundingClientRect()
    const containerRect = containerRef.current.getBoundingClientRect()
    const elementTopRelativeToContainer = elementRect.top - containerRect.top
    scrollPoint.current = elementTopRelativeToContainer + containerRef.current.scrollTop - offsetCorrection
  }

  const restorePosition = useCallback(() => {
    if (!containerRef.current || null === scrollPoint.current) return
    containerRef.current.scrollTop = scrollPoint.current
  }, [])

  useEffect(() => {
    if (!showCart && response.selected == null) {
      restorePosition()
    }
  }, [showCart, response.selected, restorePosition])

  const clearSelection = () => {
    if (showCart) {
      setShowCart(false)
      timeline.log({
        type: 'close_cart'
      })
    }
    if (response.selected != null) {
      timeline.log({
        type: 'close_popup',
        ref: response.selected
      })
    }
    onUpdateResponse({
      answers: response.answers.map(r => {
        return { ...r, selected: false }
      }),
      clicked: response.clicked
    })
  }

  const openCart = () => {
    if (response.selected != null) {
      timeline.log({
        type: 'close_popup',
        ref: response.selected
      })
      response.selected = null
    }
    if (!showCart) {
      setShowCart(true)
      timeline.log({
        type: 'open_cart'
      })
    }
  }

  const scrollTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const scrollTopFast = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [])

  const onAddToCart = (qnt: 1 | -1, id: string, subscribe?: number) => {
    const responseIndex = response.answers.findIndex(answer => answer.id === id && answer.subscribe === subscribe)
    const answer = schema.answers.find(answer => answer.id === id)
    if (responseIndex >= 0) {
      response.answers[responseIndex].v += qnt
      response.answers[responseIndex].subscribe = subscribe
    } else {
      response.answers.push({
        id: id,
        v: qnt,
        subscribe: subscribe,
        price: subscribe
          ? Math.round((answer.discounted_price || answer.price) * (1 - (answer.subscribe_percent || 0) / 100) * 100) /
            100
          : answer.discounted_price || answer.price
      })
    }
    // response.answers[index].v += qnt
    if (response.selected == null && !showCart) {
      timeline.log({
        target: '0,0',
        type: 'open_popup',
        ref: id
      })
    }
    timeline.log({
      type: showCart ? 'edit_in_cart' : 'add_to_cart',
      ref: id,
      target:
        (showCart || response.selected != null) && responseIndex >= 0
          ? response.answers[responseIndex].v.toString()
          : '1'
    })
    if (response.answers[responseIndex]?.subscribe) {
      timeline.log({
        type: showCart ? 'subscribe_edit_in_cart' : 'subscribe_add_to_cart',
        ref: id,
        target: response.answers[responseIndex].v.toString(),
        context: {
          qty: response.answers[responseIndex].v,
          weeks: response.answers[responseIndex].subscribe,
          price: response.answers[responseIndex].price
        }
      })
    }
    if (response.selected == null && !showCart) {
      timeline.log({
        type: 'close_popup',
        ref: id
      })
    }
    onUpdateResponse(response)
  }

  const onBuyNow = (qnt: number) => {
    // Buy Now is reachable from `DeliveryPanel` only when `!isSubscribe`, so the
    // resulting cart line is always one-time. Key the lookup on `(id, subscribe=undefined)`
    // so an existing Subscribe & Save line for the same product is NOT incremented
    // (widget.md §Subscribe & Save → Cart limits).
    // Snapshot selected id — `response.selected` is reset below before close_popup/buy_now
    // emit, and a null ref fails backend timeline validation (loops the question).
    const selectedId = response.selected
    const answer = schema.answers.find(a => a.id === selectedId)
    const index = response.answers.findIndex(a => a.id === selectedId && a.subscribe === undefined)
    if (index >= 0) {
      response.answers[index].v += qnt
    } else {
      response.answers.push({
        id: selectedId,
        v: qnt,
        price: answer?.discounted_price || answer?.price
      })
    }
    timeline.log({
      type: 'add_to_cart',
      ref: selectedId,
      target: (response.answers[index]?.v || qnt).toString()
    })
    // }

    if (!findability) {
      response.selected = null
    }
    timeline.log({
      type: 'close_popup',
      ref: selectedId
    })
    if (!findability) {
      setShowCart(true)
      timeline.log({
        type: 'open_cart'
      })
    }
    timeline.log({
      type: 'buy_now',
      ref: selectedId,
      target: qnt.toString()
    })
    onUpdateResponse(response)
  }

  const onSpin = (event: React.KeyboardEvent, id: string, subscribe?: number) => {
    event.stopPropagation()
    const responseIndex = response.answers.findIndex(answer => answer.id === id && answer.subscribe === subscribe)
    if (responseIndex < 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        response.answers[responseIndex].v -= 1
        break
      case 'ArrowUp':
        event.preventDefault()

        response.answers[responseIndex].v += 1
        break
    }
    timeline.log({
      type: showCart ? 'edit_in_cart' : 'add_to_cart',
      ref: id,
      target: showCart ? response.answers[responseIndex].v.toString() : '1'
    })
    if (response.answers[responseIndex].subscribe) {
      timeline.log({
        type: showCart ? 'subscribe_edit_in_cart' : 'subscribe_add_to_cart',
        ref: id,
        target: response.answers[responseIndex].v.toString(),
        context: {
          qty: response.answers[responseIndex].v,
          weeks: response.answers[responseIndex].subscribe,
          price: response.answers[responseIndex].price
        }
      })
    }
    onUpdateResponse(response)
  }

  const computeBannerIndex = (productIndex: number): number => {
    const productId = schema.answers[productIndex].id
    const candidates = schema.answers
      .map((answer, i) => ({ answer, index: i }))
      .filter(({ answer }) => answer.right_column && answer.id !== productId)
    if (0 === candidates.length) return -1
    return candidates[Math.floor(Math.random() * candidates.length)].index
  }

  const onItemSelect = (index: number, bannerPlacement?: string) => {
    if (bannerPlacement) {
      timeline.log({
        type: 'banner_click',
        ref: schema.answers[index].id,
        context: { placement: bannerPlacement }
      })
    }

    if (showCart) {
      setShowCart(false)
      timeline.log({
        type: 'close_cart'
      })
    }
    if (response.selected != null) response.selected = null
    response.selected = schema.answers[index].id

    const bannerIndex = computeBannerIndex(index)
    setRightColumnProductIndex(bannerIndex)

    timeline.log({
      target: '0,0',
      type: 'open_popup',
      ref: schema.answers[index].id
    })
    onUpdateResponse(response)
  }

  const onBannerClick = () => {
    if (-1 === rightColumnProductIndex || response.selected == null) return
    timeline.log({
      type: 'banner_click',
      ref: schema.answers[rightColumnProductIndex].id,
      context: { placement: 'product_page', source: response.selected }
    })
    onItemSelect(rightColumnProductIndex)
  }

  const onCloseItem = () => {
    scrollTopFast()
    if (response.selected != null) {
      const selectedId = response.selected
      response.selected = null
      setShowCart(false)
      timeline.log({
        type: 'close_popup',
        ref: selectedId
      })
      onUpdateResponse(response)
    }
  }

  const onHomeClick = () => {
    if (showCart) {
      setShowCart(false)
      timeline.log({
        type: 'close_cart'
      })
    }
    if (response.selected != null) {
      timeline.log({
        type: 'close_popup',
        ref: response.selected
      })
      response.selected = null
      onUpdateResponse(response)
    }
  }

  useEffect(() => {
    if (na) {
      const naResponse = response.answers.find(answer => answer.id === na.id)
      if (naResponse && naResponse.v > 0) {
        checkout()
      }
    }
  }, [response, checkout, na])

  const pvRoot: HTMLElement = document.querySelector('.surveyWidgetFullsizeContainer')
  if (theme.style?.pv && !theme.style?.flutter && !pvRoot) return null

  return (
    <Dialog
      maskClassName={`${style.lightbox} ${viewport.mobile ? style.lightboxMobile : ''} ${session.themeClassNames}`}
      appendTo={theme.style?.pv && pvRoot ? pvRoot : session.document?.body}
      onHide={() => {}}
      maximizable={true}
      maximized={true}
      visible={true}
      ref={lightboxRef}
      aria-label={t('questions', 'ec', 'amazon', 'simulation')}
    >
      <div className={`${style.externalContainer} ${viewport.mobile ? style.externalContainerMobile : ''}`}>
        <div className={`${style.container} ${viewport.mobile ? style.containerMobile : ''}`} ref={containerRef}>
          {viewport.mobile ? (
            <AppMobileHeader
              searchCondition={schema.search_condition}
              clearSelection={clearSelection}
              showCart={showCart}
              selectedIndex={response.selected != null}
              onHomeClick={onHomeClick}
            />
          ) : (
            <Header
              results={resultsCount}
              cartNumber={totals[0]}
              searchCondition={schema.search_condition}
              clearSelection={clearSelection}
              findability={findability}
              openCart={openCart}
              showArrow={!showCart && showRightColumn}
            />
          )}
          {response.selected != null ? (
            <Item
              response={response}
              schema={schema.answers.find(answer => answer.id === response.selected)}
              questionSchema={schema}
              qty={response.answers.find(answer => answer.id === response.selected)?.v}
              findability={findability}
              onClose={onCloseItem}
              onAddToCart={(qty: number, subscribe?: number) => {
                const responseIndex = response.answers.findIndex(
                  answer => answer.id === response.selected && answer.subscribe === subscribe
                )
                const answer = schema.answers.find(answer => answer.id === response.selected)
                const subscribePrice = subscribe
                  ? Math.round((answer.discounted_price || answer.price) * (1 - answer.subscribe_percent / 100) * 100) /
                    100
                  : answer.discounted_price || answer.price
                if (responseIndex >= 0) {
                  response.answers[responseIndex].v += qty || 1
                  response.answers[responseIndex].subscribe = subscribe
                } else {
                  response.answers.push({
                    id: response.selected,
                    v: qty || 1,
                    subscribe: subscribe,
                    price: subscribePrice
                  })
                }
                timeline.log({
                  type: 'add_to_cart',
                  ref: response.selected,
                  target: qty.toString()
                })
                if (subscribe) {
                  timeline.log({
                    type: 'subscribe_add_to_cart',
                    ref: response.selected,
                    target: qty.toString(),
                    context: { qty: qty, weeks: subscribe, price: subscribePrice }
                  })
                }
                if (findability && response.selected != null) {
                  timeline.log({
                    type: 'close_popup',
                    ref: response.selected
                  })
                }
                onUpdateResponse(response)
              }}
              onBuyNow={onBuyNow}
              totals={totals}
              scrollTop={scrollTopFast}
              onItemSelect={onItemSelect}
              rightColumnProductIndex={rightColumnProductIndex}
              onBannerClick={onBannerClick}
              onProductAction={(qnt, index, subscribe) => onAddToCart(qnt, index, subscribe)}
              onSpin={(event, index) => onSpin(event, index)}
              saveScrollPoint={saveScrollPoint}
            />
          ) : showCart ? (
            <Cart
              totals={totals}
              response={response}
              schema={schema}
              onUpdateResponse={onUpdateResponse}
              showCart={openCart}
              closeCart={onHomeClick}
              checkout={checkout}
              scrollTop={scrollTopFast}
            />
          ) : (
            <div className={style.view}>
              {!viewport.mobile && <Aside />}
              <div className={style.content}>
                {/* <div
                  className={style.results}
                  dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'amazon', 'results') }}
                /> */}
                <Main
                  schema={schema}
                  mainWidth={mainWidth}
                  ceilWidth={ceilWidth}
                  response={response}
                  productsPerRow={productsPerRow}
                  onProductAction={(qnt: 1 | -1, index: string, subscribe?: number) =>
                    onAddToCart(qnt, index, subscribe)
                  }
                  onSpin={(event, index) => onSpin(event, index)}
                  onItemSelect={(index, placement) => onItemSelect(index, placement)}
                  saveScrollPoint={saveScrollPoint}
                />
              </div>
            </div>
          )}
          {viewport.mobile ? (
            <AppMobileFooter
              openCart={openCart}
              closeCart={onHomeClick}
              cartNumber={totals[0]}
              selectedIndex={response.selected != null}
              closeItem={onCloseItem}
              cartDisabled={findability}
              showHint={() => {
                setNeedHint(true)
              }}
              closeSimulation={setClosed}
            />
          ) : (
            <Footer scrollTop={scrollTop} />
          )}
        </div>
        {showRightColumn && (!showCart || response.selected != null) ? (
          <RightColumn
            response={response}
            subTotal={totals[1]}
            openCart={openCart}
            schema={schema}
            onProductAction={(qnt: 1 | -1, index: string, subscribe?: number) => onAddToCart(qnt, index, subscribe)}
            onItemSelect={index => onItemSelect(index)}
          />
        ) : (
          ''
        )}
      </div>
      <ExitSimulation
        setClosed={setClosed}
        needExternalHint={needHint}
        setExternalHint={viewport.mobile ? setNeedHint : null}
        hintText={schema.instruction}
      />
    </Dialog>
  )
}

export default AmazonView
